import { apiSlice } from './apiSlice';
import type {
  CaptureGardenPlanRequest,
  CaptureGardenPlanResponse,
  GardenPlan,
  GardenPlanPreview,
  GardenPlanVersion,
  GardenPlanSequence,
  GardenPlanSequenceStep,
} from '../types/gardenPlans';

export const gardenPlansApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listGardenPlans: builder.query<GardenPlan[], { status?: 'active' | 'archived' } | void>({
      query: (args) => `/garden-plans${args && args.status ? `?status=${args.status}` : ''}`,
      transformResponse: (response: { plans: GardenPlan[] }) => response.plans || [],
      providesTags: (result) => result
        ? [...result.map((plan) => ({ type: 'GardenPlan' as const, id: plan.id })), { type: 'GardenPlan', id: 'LIST' }]
        : [{ type: 'GardenPlan', id: 'LIST' }],
    }),
    getGardenPlan: builder.query<{ plan: GardenPlan; versions: GardenPlanVersion[] }, string>({
      query: (planId) => `/garden-plans/${planId}`,
      providesTags: (_result, _error, planId) => [{ type: 'GardenPlan', id: planId }],
    }),
    captureGardenPlan: builder.mutation<CaptureGardenPlanResponse, CaptureGardenPlanRequest>({
      query: (body) => ({ url: '/garden-plans/from-save', method: 'POST', body }),
      invalidatesTags: [{ type: 'GardenPlan', id: 'LIST' }],
    }),
    archiveGardenPlan: builder.mutation<{ plan: GardenPlan }, string>({
      query: (planId) => ({ url: `/garden-plans/${planId}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, planId) => [
        { type: 'GardenPlan', id: planId },
        { type: 'GardenPlan', id: 'LIST' },
      ],
    }),
    previewGardenPlan: builder.mutation<
      GardenPlanPreview,
      { planId: string; versionId: string; targetYear: number; measurementSystem?: string }
    >({
      query: ({ planId, versionId, ...body }) => ({
        url: `/garden-plans/${planId}/versions/${versionId}/preview`,
        method: 'POST',
        body: { ...body, layers: { topology: true, plantings: false } },
      }),
    }),
    listGardenPlanSequences: builder.query<GardenPlanSequence[], void>({
      query: () => '/garden-plan-sequences',
      transformResponse: (response: { sequences: GardenPlanSequence[] }) => response.sequences || [],
    }),
    createGardenPlanSequence: builder.mutation<
      { sequence: GardenPlanSequence; version: Record<string, unknown> },
      { name: string; description?: string; repeatMode: 'once' | 'cycle'; steps: GardenPlanSequenceStep[] }
    >({
      query: (body) => ({ url: '/garden-plan-sequences', method: 'POST', body }),
      invalidatesTags: [{ type: 'GardenPlan', id: 'LIST' }],
    }),
  }),
});

export const {
  useListGardenPlansQuery,
  useGetGardenPlanQuery,
  useCaptureGardenPlanMutation,
  useArchiveGardenPlanMutation,
  usePreviewGardenPlanMutation,
  useListGardenPlanSequencesQuery,
  useCreateGardenPlanSequenceMutation,
} = gardenPlansApi;
